
// TED Code Mappings
// These maps provide human-readable descriptions for various coded fields in TED notices

// Procedure type mappings
export const procedureTypeMap: Record<string, string> = {
  'open': 'Open Procedure',
  'restricted': 'Restricted Procedure',
  'competitive-negotiation': 'Competitive Procedure with Negotiation',
  'competitive-dialogue': 'Competitive Dialogue',
  'innovation': 'Innovation Partnership',
  'negotiated-no-prior-publication': 'Negotiated Procedure without Prior Publication',
  'negotiated-with-prior-publication': 'Negotiated Procedure with Prior Publication',
  'negotiated-qualified-tenderers': 'Negotiated Procedure with Selected Qualified Tenderers',
  'design-contest': 'Design Contest',
  'direct-award': 'Direct Award',
  'concession-award-prior-comp': 'Concession Award with Prior Competition',
  'concession-award-no-prior-comp': 'Concession Award without Prior Competition'
};

// Notice type mappings
export const noticeTypeMap: Record<string, string> = {
  'cn': 'Contract Notice',
  'cn-standard': 'Contract Notice (Standard)',
  'can': 'Contract Award Notice',
  'pin': 'Prior Information Notice',
  'pin-only': 'Prior Information Notice (Market Notice Only)',
  'pin-competition': 'Prior Information Notice (Call for Competition)',
  'pin-buyer': 'Prior Information Notice (Buyer Profile)',
  'veat': 'Voluntary Ex Ante Transparency Notice',
  'subco': 'Subcontract Notice',
  'social': 'Social and Other Specific Services',
  'cn-social': 'Contract Notice for Social and Other Specific Services',
  'can-social': 'Contract Award Notice for Social and Other Specific Services',
  'pin-social': 'Prior Information Notice for Social and Other Specific Services',
  'concess': 'Concession Notice',
  'cn-desg': 'Design Contest Notice',
  'can-desg': 'Design Contest Results Notice',
  'mod': 'Modification Notice',
  'buyer': 'Buyer Notice'
};

// Main activity mappings for contracting authorities
export const authorityActivityMap: Record<string, string> = {
  'gen-pub': 'General Public Services',
  'defence': 'Defence',
  'pub-ord': 'Public Order and Safety',
  'environment': 'Environment Protection',
  'eco-aff': 'Economic and Financial Affairs',
  'health': 'Health',
  'housing': 'Housing and Community Amenities',
  'social': 'Social Protection',
  'leisure': 'Recreation, Culture and Religion',
  'education': 'Education',
  'other-act': 'Other Activity'
};

// Main activity mappings for contracting entities
export const entityActivityMap: Record<string, string> = {
  'elect': 'Electricity',
  'gas-heat': 'Gas and Heat Production',
  'prosp': 'Exploration and Extraction',
  'water': 'Water',
  'trans-serv': 'Transport Services',
  'postal': 'Postal Services',
  'airport': 'Airport-Related Activities',
  'port': 'Port-Related Activities',
  'rail-serv': 'Railway Services',
  'urban-rail': 'Urban Railway Transport',
  'other-entity-act': 'Other Activity Entity'
};

// Combined main activity map for easy lookup
export const mainActivityMap: Record<string, string> = {
  ...authorityActivityMap,
  ...entityActivityMap
};

// Legal basis mappings
export const legalBasisMap: Record<string, string> = {
  '32014L0024': 'Directive 2014/24/EU (Classical)',
  '32014L0025': 'Directive 2014/25/EU (Utilities)',
  '32014L0023': 'Directive 2014/23/EU (Concession)',
  '32009L0081': 'Directive 2009/81/EC (Defence)',
  '32018R1046': 'Financial Regulation'
};

// Buyer legal type mappings
export const buyerLegalTypeMap: Record<string, string> = {
  'ra': 'Regional or Local Authority',
  'la': 'Local Authority', // Added "la" code
  'body-pl': 'Body Governed by Public Law',
  'ministry': 'Ministry or Other National/Federal Authority',
  'natag': 'National or Federal Agency',
  'regag': 'Regional or Local Agency',
  'association': 'Association of Contracting Authorities',
  'eu-institution': 'European Institution/Agency',
  'org-int': 'International Organization',
  'other': 'Other Type of Buyer'
};

// Contract nature mappings
export const contractNatureMap: Record<string, string> = {
  'works': 'Works',
  'supplies': 'Supplies',
  'services': 'Services',
  'combined': 'Mixed Contract'
};

// Strategic procurement mappings
export const strategicProcurementMap: Record<string, string> = {
  'none': 'None',
  'social': 'Social Considerations',
  'env': 'Environmental Considerations',
  'innov': 'Innovation Considerations'
};

// Environmental impact approach mappings
export const environmentalImpactMap: Record<string, string> = {
  'co2': 'CO2 Emissions',
  'costs': 'Environmental Costs',
  'emissions': 'Other Emissions',
  'energy': 'Energy Efficiency',
  'other': 'Other Environmental Impact',
  'none': 'No Environmental Impact Considered'
};

// Document access restriction mappings
export const documentAccessMap: Record<string, string> = {
  'restricted': 'Full Restrictions',
  'partially': 'Partial Restrictions',
  'not-restricted': 'No Restrictions',
  'restricted-document': 'Restricted Access',
  'BT-14-Lot': 'Restricted Access (Lot)',
  'BT-14-Part': 'Restricted Access (Part)'
};

// Subcontracting obligation mappings
export const subcontractingMap: Record<string, string> = {
  'no-value': 'No Specific Value/Proportion Required',
  'min-value': 'Minimum Value Required',
  'max-value': 'Maximum Value Allowed',
  'min-max-value': 'Minimum and Maximum Values Specified',
  'none': 'No Subcontracting Obligations'
};

// Helper function to get the description for a TED code based on the field key and value
export const getTedCodeDescription = (key: string, value: string): string | undefined => {
  if (!value) return undefined;
  
  // Special handling for array values
  if (Array.isArray(value)) {
    // For buyer legal type codes that might be arrays
    if (key === 'buyer-legal-type' || key === 'AA' || key === 'BT-11-Procedure-Buyer') {
      if (value.length > 0 && buyerLegalTypeMap[value[0]]) {
        return buyerLegalTypeMap[value[0]];
      }
    }
    
    // For procedure type codes that might be arrays
    if (key === 'procedure-type' || key === 'PR' || key === 'BT-105-Procedure') {
      if (value.length > 0 && procedureTypeMap[value[0]]) {
        return procedureTypeMap[value[0]];
      }
    }
    
    // Return undefined for empty arrays or if no mapping exists
    return undefined;
  }
  
  // Check for procedure type
  if (key === 'procedure-type' || key === 'PR' || key === 'BT-105-Procedure') {
    return procedureTypeMap[value];
  }
  
  // Check for notice type
  if (key === 'notice-type' || key === 'BT-02-notice') {
    return noticeTypeMap[value];
  }
  
  // Check for main activity
  if (key === 'main-activity' || key === 'MA' || 
      key === 'authority-main-activity' || key === 'BT-10-Procedure-Buyer' ||
      key === 'entity-main-activity' || key === 'BT-610-Procedure-Buyer') {
    return mainActivityMap[value];
  }
  
  // Check for buyer legal type
  if (key === 'buyer-legal-type' || key === 'AA' || key === 'BT-11-Procedure-Buyer') {
    return buyerLegalTypeMap[value];
  }
  
  // Check for contract nature
  if (key === 'contract-nature' || key === 'NC' || key.startsWith('BT-531')) {
    return contractNatureMap[value];
  }
  
  // Check for legal basis
  if (key === 'legal-basis' || key === 'DI' || key === 'legal-basis-notice') {
    return legalBasisMap[value];
  }
  
  // Check for strategic procurement
  if (key === 'strategic-procurement-lot' || key === 'BT-06-Lot') {
    return strategicProcurementMap[value];
  }
  
  // Check for environmental impact
  if (key === 'environmental-impact-lot' || key === 'BT-774-Lot') {
    return environmentalImpactMap[value];
  }
  
  // Check for document access restrictions
  if (key === 'document-restricted-lot' || key === 'BT-14-Lot' || key === 'BT-14-Part') {
    return documentAccessMap[value];
  }
  
  // Check for subcontracting obligations
  if (key === 'subcontracting-obligation-lot' || key === 'BT-65-Lot') {
    return subcontractingMap[value];
  }

  return undefined;
};
